<!--
  市场核实接收人设置（REQ-2026-601171 §E：「接收人做成后台可配置的角色/人员列表」）。

  后端接口（GET/POST /cust/childFollowup/marketReceivers）第一版就有，但**没有任何页面**，运营配不了 ——
  测试环境里那份名单是测试同事直接改库写进去的。

  ⚠️ 接收人 = 选中的「人员」**并上**选中的「角色」下的所有人（并集，不是交集）。
     口径以 app-server 为准：它用这份名单判断"你能不能进核实页"；管理端用同一份名单决定"通知发给谁"。
  ⚠️ 名单里的人必须**在 App 里有身份**（客户 / 技术老师）才收得到通知、进得了核实页 ——
     纯后台账号登不进 App，配了也没用。保存时不拦（运营可能就是要先配上），只在界面上提示。
-->
<template>
  <el-dialog title="市场核实接收人设置" :visible.sync="visible" width="560px" :close-on-click-modal="false" append-to-body>
    <div v-loading="loading">
      <el-alert
        type="info"
        :closable="false"
        show-icon
        class="mb10"
        title="回访判定异常后，系统会给下面名单里的人发「子账号待核实」通知，他们在 App 的「消息 → 其他通知」里点开即可处理。名单 = 指定人员 + 指定角色下的所有人。"
      />
      <el-form label-width="90px">
        <el-form-item label="指定人员">
          <el-select
            v-model="form.userIds"
            multiple
            filterable
            remote
            reserve-keyword
            placeholder="输入姓名 / 账号 / 手机号搜索"
            :remote-method="searchUsers"
            :loading="userLoading"
            style="width: 100%"
          >
            <!--
              BUG-2026-742350：只显示"姓名 + 手机号"分不清人。生产 sys_user 未删 52437 行里同名的有 14205 个，
              搜「尤文猛」出 4 条、其中一条还没手机号，而最像本人的那条恰恰是唯一收不到通知的纯后台账号。
              所以这里补两样：**登录名**（区分 real_name 相同/user_name 不同的账号）与 **App 身份**。
              无身份的**不禁用**（运营可能就是要先占位），但标红 + 明确写"收不到通知"，保存时再二次确认。
            -->
            <el-option v-for="u in userOptions" :key="u.id" :label="userLabel(u)" :value="u.id">
              <span :class="{ 'no-app': u.hasAppIdentity === false }">{{ userLabel(u) }}</span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="指定角色">
          <el-select v-model="form.roleCodes" multiple filterable placeholder="选择角色（该角色下的所有人都会收到）" style="width: 100%">
            <el-option v-for="r in roleOptions" :key="r.code" :label="r.roleName + '（' + r.code + '）'" :value="r.code" />
          </el-select>
        </el-form-item>
      </el-form>
      <div class="tip">
        ⚠️ 两项都留空 = 没有任何人会收到通知，异常任务会一直停在「待市场核实」。<br>
        ⚠️ 接收人需要在 App 里有身份（业务线客户 / 技术老师）；纯后台账号登不进 App，收不到。
      </div>
    </div>
    <div slot="footer">
      <el-button size="small" @click="visible = false">取消</el-button>
      <el-button size="small" type="primary" :loading="saving" @click="handleSave">保存</el-button>
    </div>
  </el-dialog>
</template>

<script>
import childFollowupApi from '@/api/cust/childFollowup'
import userApi from '@/api/sys/user'
import sysRoleApi from '@/api/sys/sysRole'

export default {
  name: 'MarketReceivers',
  data() {
    return {
      visible: false,
      loading: false,
      saving: false,
      userLoading: false,
      form: { userIds: [], roleCodes: [] },
      userOptions: [],
      roleOptions: []
    }
  },
  methods: {
    open() {
      this.visible = true
      this.load()
    },
    /**
     * 标签四段：姓名（登录名 xxx）手机号 · App 身份。
     * 登录名单独给出来是因为生产上有 real_name=尤文猛 而 user_name=侯菲 这种账号，
     * 只看姓名分不清；括号里**必须带"登录名"三个字** —— 生产 148062 那条账号的
     * user_name 恰好是另一个账号的手机号（18854881999），裸写会被当成它的手机号。
     * 身份未知时（接口还没回来）**不写"无身份"**，留空，免得把"还不知道"说成"确定收不到"。
     */
    userLabel(u) {
      const name = u.realName || u.userName || ('#' + u.id)
      const login = u.userName && u.userName !== u.realName ? '（登录名 ' + u.userName + '）' : ''
      const head = [name + login, u.userPho || '无手机号'].join(' ')
      return u.identityText ? head + ' · ' + u.identityText : head
    },
    /**
     * 给一批选项补上 App 身份。判据来自后端，前端只负责显示。
     * 查不到身份信息时保持 undefined（= 未知），不当作"没有身份"。
     *
     * 🔴 BUG-2026-268341：**绝对不要把它挂进弹窗主流程的 Promise 链**。
     * 身份是辅助信息，姓名/手机号不依赖它。之前 `loadSelectedUsers` / `searchUsers`
     * 把它 `return` 进了链里，于是这一个请求不 settle（不是失败、不是 abort，就是不回），
     * 外层的 `.finally` 就不执行 —— 整个弹窗一直转圈、连搜索框都点不进去。
     * 全局 `timeout` 是 500000（8 分多钟），所以不是永久卡死，但对用户等于卡死。
     * 现在调用方一律**不 await、不 return**，它自己回来了再补齐。
     *
     * 另外给这一条请求**单独设了 15 秒超时**（见 api/cust/childFollowup.js）：
     * 辅助信息等 8 分钟没有任何意义。⚠️ 只改这一条，不动全局值。
     *
     * 迟到的响应是安全的：这里只对**当前** userOptions 里 id 对得上的行打补丁，
     * 期间若已换了一批搜索结果，旧响应匹配不上任何行，等于什么都没做。
     */
    fillIdentities(list) {
      const ids = (list || []).map(u => u.id).filter(i => i)
      if (!ids.length) return Promise.resolve()
      return childFollowupApi.fetchReceiverIdentities(ids).then(({ data }) => {
        const map = {}
        ;(data || []).forEach(i => { map[i.userId] = i })
        this.userOptions = this.userOptions.map(u => {
          const hit = map[u.id]
          return hit ? Object.assign({}, u, { hasAppIdentity: hit.hasAppIdentity, identityText: hit.identityText }) : u
        })
      }).catch(error => {
        // 故意不弹提示：身份是辅助信息，失败时界面表现为"不显示身份"，与"还没查到"一致，
        // 这对用户是正确的（宁可不说，也不能把未知说成"收不到"）。
        // 但**排查的人要能看见**——不留痕的话，"接口挂了"和"这批人确实没身份"
        // 在控制台里也长得一模一样。所以这里打一行，别换成裸 catch。
        console.warn('[MarketReceivers] 身份信息获取失败，本次选项不显示 App 身份', error && (error.message || error))
      })
    },
    load() {
      this.loading = true
      Promise.all([
        childFollowupApi.fetchMarketReceivers(),
        // 角色数量是几十个的量级，一次取完做本地筛选
        sysRoleApi.fetchList({ pageNo: 1, pageSize: 500 })
      ]).then(([cfg, roles]) => {
        const data = (cfg && cfg.data) || {}
        this.form.userIds = data.userIds || []
        this.form.roleCodes = data.roleCodes || []
        // 只留有 code 的角色：名单存的是 code，没有 code 的角色选了也匹配不上
        this.roleOptions = (((roles && roles.data) || {}).list || []).filter(r => r.code)
        return this.loadSelectedUsers()
      }).catch(error => {
        this.$common && this.$common.showNotifyError(error)
      }).finally(() => { this.loading = false })
    },
    /**
     * 已选人员要有名字可显示。远程搜索的下拉框里，选项不在 options 里的话只会显示一个光秃秃的 id，
     * 运营看不出名单里到底是谁。人数是个位数，逐个取详情即可。
     */
    loadSelectedUsers() {
      const ids = this.form.userIds || []
      if (!ids.length) {
        this.userOptions = []
        return Promise.resolve()
      }
      return Promise.all(ids.map(id => userApi.fetchById(id).then(({ data }) => data).catch(() => null)))
        .then(list => {
          this.userOptions = list.filter(i => i && i.id).map(i => ({
            id: i.id, realName: i.realName, userName: i.userName, userPho: i.userPho
          }))
          // 不 return：身份慢/不回都不能挡住弹窗（BUG-2026-268341）
          this.fillIdentities(this.userOptions)
        })
    },
    searchUsers(keyword) {
      if (!keyword) return
      this.userLoading = true
      userApi.fetchList({ keyword, pageNo: 1, pageSize: 20 }).then(({ data }) => {
        const found = (data && data.list) || []
        // 已选中的要留在 options 里，否则一搜索别的关键字，已选项的标签就退化成 id
        const selected = this.userOptions.filter(u => this.form.userIds.includes(u.id))
        const merged = selected.slice()
        found.forEach(u => { if (!merged.some(m => m.id === u.id)) merged.push(u) })
        this.userOptions = merged
        // 不 return：搜索结果先显示出来，身份回来了再补（BUG-2026-268341）
        this.fillIdentities(found)
      }).finally(() => { this.userLoading = false })
    },
    handleSave() {
      const save = () => {
        this.saving = true
        childFollowupApi.saveMarketReceivers({ userIds: this.form.userIds, roleCodes: this.form.roleCodes })
          .then(() => {
            this.$common.showNotify('success', '成功', '接收人已保存')
            this.visible = false
          })
          .catch(error => this.$common.showNotifyError(error))
          .finally(() => { this.saving = false })
      }
      if (!this.form.userIds.length && !this.form.roleCodes.length) {
        this.$confirm('名单为空：之后的异常任务不会通知任何人，确定保存吗？', '提示', { type: 'warning' })
          .then(save).catch(() => {})
        return
      }
      // BUG-2026-742350：选了没有 App 身份的账号不硬拦（可能就是要先占位），但不能静默 ——
      // 原先的表现是"名单配了、通知永远发不出去"，页面毫无反应，只有后端一行 warn 日志。
      // 只对**确定没有身份**的弹（hasAppIdentity === false）；身份未知的不弹，别把"没查到"说成"收不到"。
      const noApp = this.userOptions.filter(u => this.form.userIds.includes(u.id) && u.hasAppIdentity === false)
      if (noApp.length) {
        this.$confirm(
          '以下 ' + noApp.length + ' 人没有 App 身份（纯后台账号），配进名单也收不到通知：' +
          noApp.map(u => this.userLabel(u)).join('；') + '。仍要保存吗？',
          '提示', { type: 'warning' })
          .then(save).catch(() => {})
        return
      }
      save()
    }
  }
}
</script>

<style scoped>
.tip { color: #909399; font-size: 12px; line-height: 1.8; padding-left: 90px; }
.no-app { color: #F56C6C; }
.mb10 { margin-bottom: 10px; }
</style>
